import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddTaskModal from '../components/AddTaskModal';
import OnboardingCelebration from '../components/OnboardingCelebration';
import Tasks from '../components/Tasks';
import SurveyModal from '../components/SurveyModal';
import { useSurvey } from '../hooks/useSurvey';
import api from '../api/api';
import { useLookups } from '../context/LookupContext';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import DashboardSkeleton from '../components/layout/DashboardSkeleton';
import { User, Target, Briefcase, Calendar, Plus, Trash2, X, Check } from 'lucide-react';

const stripTaskPrefix = (value) => (typeof value === 'string' ? value.replace(/^\s*\d+\.\s*/, '').trim() : value);

const normalizeDifficultyLabel = (value) => {
    const key = String(value || 'M').trim().toUpperCase();
    const map = {
        S: 'Small',
        M: 'Medium',
        L: 'Large',
        E: 'Small',
        H: 'Large',
        SMALL: 'Small',
        MEDIUM: 'Medium',
        LARGE: 'Large',
        EASY: 'Small',
        HARD: 'Large'
    };
    return map[key] || String(value || 'Medium');
};

const normalizeRecommendationList = (items) => {
    if (!Array.isArray(items)) return [];
    return items.map((item) => {
        if (item && typeof item === 'object') {
            return {
                ...item,
                task: stripTaskPrefix(item.task)
            };
        }
        return {
            task: stripTaskPrefix(item),
            diff: 'Suggested'
        };
    });
};

const getFocusAreaInitial = (task) => {
    const raw =
        task?.FocusAreaDescription ||
        task?.FocusAreaDesciption ||
        task?.FocusArea ||
        task?.focusAreaDescription ||
        task?.area ||
        'General';
    return String(raw).trim().charAt(0).toUpperCase() || 'G';
};

const isPendingCoachRecommendation = (item) => {
    const status = String(item?.Status || '').trim().toLowerCase();
    return status === '' || status === '-' || status === 'pending';
};

const getCoachRecommendationStatusLabel = (item) => {
    return isPendingCoachRecommendation(item) ? 'Pending' : String(item?.Status || '').trim() || 'Pending';
};

const GOAL_TRACKER_AREAS = [
    { label: 'Performance', matches: ['performance', 'job performance'] },
    { label: 'Skilling', matches: ['skilling'] },
    { label: 'Networking', matches: ['networking'] },
    { label: 'Giving Back', matches: ['giving back'] },
    { label: 'Feedback', matches: ['feedback'] }
];

const normalizeFocusAreaKey = (value) => String(value || '').trim().toLowerCase();

const buildGoalCounts = (tasks) => {
    return GOAL_TRACKER_AREAS.map((area) => {
        const count = (Array.isArray(tasks) ? tasks : []).filter((task) => {
            const focusAreaName = normalizeFocusAreaKey(task?.FocusAreaName || task?.FocusArea || task?.focusAreaName);
            return area.matches.some((match) => focusAreaName.includes(match));
        }).length;

        return {
            name: area.label,
            count,
        };
    });
};

// The "Complete Onboarding" summary row is excluded from the visible checklist.
// Its TaskID (2281) is stable/legacy, so matching it by id is safe. The other
// onboarding tasks are NOT matched by id anywhere — their TaskIDs differ across
// environments (dev was hand-edited, prod follows migrations), so routing keys
// off the task text instead (see handleCompleteOnboardingClick).
const ONBOARDING_TASK_ID_COMPLETE = '2281';

const isOnboardingTaskDone = (task) =>
    Boolean(task?.isComplete) || String(task?.status || task?.Status || '').toLowerCase() === 'completed';

// Button style helper
const btnOutline = (isDarkMode) => isDarkMode 
  ? "rounded-lg bg-blue-700 text-slate-200 border border-slate-600 px-4 py-2 text-sm font-semibold hover:bg-blue-600 transition-colors"
  : "rounded-lg bg-blue-100 text-amber-900 px-4 py-2 text-sm font-semibold hover:bg-blue-200 transition-colors";

const Dashboard = () => {
    const { lookups } = useLookups();
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { pendingSurvey, checkSurveys, dismissSurvey, completeSurvey } = useSurvey();
    const surveyChecked = useRef(false);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState({ name: "", info: [], profilePicture: null });
    // An expired/broken picture URL falls back to the placeholder icon
    const [avatarBroken, setAvatarBroken] = useState(false);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [, setFocusAreas] = useState([]);
    const [tasks, setTasks] = useState([]);
const visibleTasks = tasks.filter((task) => String(task.Status ?? task.status ?? '').trim().toLowerCase() !== 'completed');
    const [goals, setGoals] = useState([]);
    const [recommendedTasks, setRecommendedTasks] = useState({ aiCoach: [], profileMatch: [] });
    const [recommendedMeta, setRecommendedMeta] = useState({ persona: 'Your AI Coach', source: 'none' });
    const [isRegeneratingRecommendations, setIsRegeneratingRecommendations] = useState(false);
    const [recommendedTaskPrefill, setRecommendedTaskPrefill] = useState(null);
    const [pendingAcceptedRecommendation, setPendingAcceptedRecommendation] = useState(null);
    const [recommendedJobs, setRecommendedJobs] = useState([]);
    const [onboardingDashboardTasks, setOnboardingDashboardTasks] = useState([]);
    const [coachRecommendations, setCoachRecommendations] = useState([]);
    const [coachActionLoadingId, setCoachActionLoadingId] = useState(null);

    // Handle opening the add task modal
    const handleAddTask = () => {
        setRecommendedTaskPrefill(null);
        setPendingAcceptedRecommendation(null);
        setShowAddTaskModal(true);
    };

    const handleCloseAddTaskModal = () => {
        if (pendingAcceptedRecommendation?.kind === 'coach') {
            const shouldClose = window.confirm('This coach recommendation will stay pending until you save the task. Close this window?');
            if (!shouldClose) {
                return;
            }
        }
        setShowAddTaskModal(false);
        setRecommendedTaskPrefill(null);
        setPendingAcceptedRecommendation(null);
    };

    // Handle task creation success
    const handleTaskCreated = async (newTask) => {
        console.log('New task created:', newTask);
        await fetchTasks();
        // Refresh onboarding status too -- adding a task can be what satisfies
        // "Add 3 Recommended Tasks", so the widget/counter/celebration need
        // fresh data right away rather than waiting for the next page load.
        fetchDashboardPanels();

        if (pendingAcceptedRecommendation) {
            if (pendingAcceptedRecommendation.kind === 'recommended') {
                const { sectionKey, idx, suggestionId } = pendingAcceptedRecommendation;
                if (suggestionId) {
                    try {
                        await api.put(`/task/bb-suggestions/${suggestionId}/respond`, { action: 'Accepted' });
                    } catch (err) {
                        console.error('Failed to finalize BB recommendation acceptance:', err);
                    }
                }

                setRecommendedTasks((previous) => {
                    const sectionItems = Array.isArray(previous[sectionKey]) ? previous[sectionKey] : [];
                    const nextSectionItems = sectionItems.filter((_, index) => index !== idx);
                    return {
                        ...previous,
                        [sectionKey]: nextSectionItems
                    };
                });
            }

            if (pendingAcceptedRecommendation.kind === 'coach') {
                const { suggestionId } = pendingAcceptedRecommendation;
                try {
                    await api.put(`/mentor/coach-suggestions/${suggestionId}/respond`, { action: 'Accepted' });
                    setCoachRecommendations((previous) => previous.map((item) => (
                        item?.SuggestionID === suggestionId
                            ? { ...item, Status: 'Accepted' }
                            : item
                    )));
                } catch (err) {
                    console.error('Failed to finalize coach recommendation acceptance:', err);
                }
            }
        }

        setRecommendedTaskPrefill(null);
        setPendingAcceptedRecommendation(null);
    };

    // Meetup events are shown as a static "coming soon" message for now (see
    // the Upcoming Meetup Events card below) — the setter is kept since
    // fetchDashboardPanels still stores whatever the API returns, but nothing
    // reads the value yet.
    const [, setUpcomingMeetups] = useState([]);
    const [showCelebration, setShowCelebration] = useState(false);

    const recommendedSections = [
        { key: 'aiCoach', title: recommendedMeta.persona || 'Your AI Coach' },
        { key: 'profileMatch', title: 'Matched to your profile' }
    ];

    // The "Complete Onboarding" row itself is excluded from the visible list below
    // since the button shows its progress directly.
    const visibleOnboardingTasks = onboardingDashboardTasks.filter(
        (task) => task?.taskId !== ONBOARDING_TASK_ID_COMPLETE
    );
    // Progress is counted across whatever onboarding tasks exist — admins can
    // add/remove them — so the denominator is dynamic, not a fixed number.
    const onboardingDoneCount = visibleOnboardingTasks.filter(isOnboardingTaskDone).length;
    const onboardingTotalCount = visibleOnboardingTasks.length;

const allOnboardingTasksDone =
    onboardingDashboardTasks.length > 0 &&
    onboardingDashboardTasks.every(isOnboardingTaskDone);

    // Send the user to the first incomplete onboarding step. Routed by task text
    // (not TaskID) so it can't break when the definitions' ids differ between
    // dev and prod; unknown tasks fall through to the onboarding/preferences page.
    const handleCompleteOnboardingClick = () => {
        const next = visibleOnboardingTasks.find((task) => !isOnboardingTaskDone(task));
        if (!next) return;
        const text = `${next.title || ''} ${next.note || ''}`.toLowerCase();
        if (text.includes('career success') || text.includes('assessment')) {
            navigate('/career-success-index/user');
        } else if (text.includes('recommend') || text.includes('pick')) {
            document.getElementById('recommended-tasks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            navigate('/preferences');
        }
    };

    const maxGoalCount = Math.max(1, ...goals.map((goal) => goal.count || 0));

    const fetchTasks = async () => {
        try {
            const response = await api.get("/task/activetasks");
            if (Array.isArray(response.data)) {
                setTasks(response.data);
            }
        } catch (err) {
            console.error("Error fetching tasks:", err);
        }
    };

    const fetchRecommendedTasks = async (forceRefresh = false) => {
        try {
            const response = await api.get(`/task/recommendations?count=20${forceRefresh ? '&refresh=true' : ''}`);
            const recommendations = Array.isArray(response.data?.aiCoach) ? response.data.aiCoach : [];
            const primary = normalizeRecommendationList(recommendations.slice(0, 10));
            const secondary = normalizeRecommendationList(recommendations.slice(10, 20));

            setRecommendedMeta({
                persona: response.data?.persona || 'Your AI Coach',
                source: response.data?.source || 'none'
            });
            const nextRecommendedTasks = {
                aiCoach: primary,
                profileMatch: secondary.length ? secondary : primary
            };
            setRecommendedTasks(nextRecommendedTasks);
        } catch (err) {
            console.error('Error fetching recommended tasks:', err);
            setRecommendedTasks({ aiCoach: [], profileMatch: [] });
        }
    };

    const handleRegenerateRecommendedTasks = async () => {
        setIsRegeneratingRecommendations(true);
        await fetchRecommendedTasks(true);
        setIsRegeneratingRecommendations(false);
    };

    const fetchDashboardPanels = async () => {
        try {
            const response = await api.get('/user/dashboard-data');
            const newOnboardingTasks = Array.isArray(response.data?.onboardingTasks) ? response.data.onboardingTasks : [];
            setRecommendedJobs(Array.isArray(response.data?.recommendedJobs) ? response.data.recommendedJobs : []);
            setOnboardingDashboardTasks(newOnboardingTasks);
            setUpcomingMeetups(Array.isArray(response.data?.meetupEvents) ? response.data.meetupEvents : []);
            setCoachRecommendations(
                Array.isArray(response.data?.coachRecommendations)
                        ? response.data.coachRecommendations
                    : []
            );

            // Show the completion celebration once per user the first time we see
            // onboarding fully done, and reset that so a later disconnect + redo
            // (e.g. LinkedIn) celebrates again instead of staying silent forever.
            const celebrationOwnerId = user?.clientid ?? user?.id ?? user?.email;
            if (celebrationOwnerId) {
                const storageKey = `onboarding_celebrated_${celebrationOwnerId}`;
                const nowAllDone = newOnboardingTasks.length > 0 && newOnboardingTasks.every(isOnboardingTaskDone);
                if (nowAllDone) {
                    if (localStorage.getItem(storageKey) !== '1') {
                        setShowCelebration(true);
                        localStorage.setItem(storageKey, '1');
                    }
                } else {
                    localStorage.removeItem(storageKey);
                }
            }
        } catch (err) {
            console.error('Error fetching dashboard panel data:', err);
            setRecommendedJobs([]);
            setOnboardingDashboardTasks([]);
            setUpcomingMeetups([]);
            setCoachRecommendations([]);
        }
    };

    const handleOpenRecommendedJob = (job) => {
        if (!job?.id) {
            navigate('/jobbrowser');
            return;
        }

        navigate('/jobbrowser', {
            state: {
                openJobId: job.id,
                openJobTitle: job.title || '',
                openJobCompany: job.company || '',
            },
        });
    };

    const handleAcceptRecommendedTask = (sectionKey, idx, task) => {
        setRecommendedTaskPrefill({
            Title: stripTaskPrefix(task?.task),
            FocusArea: task?.FocusAreaDescription || task?.FocusAreaDesciption || task?.focusAreaDescription || task?.area || 'General',
            FocusAreaID: task?.FocusAreaID || task?.focusAreaId || '',
            Description: task?.description || ''
        });
        setPendingAcceptedRecommendation({ kind: 'recommended', sectionKey, idx, suggestionId: task?.SuggestionsID });
        setShowAddTaskModal(true);
    };

    const handleRespondToCoachRecommendation = async (suggestion, action) => {
        setCoachActionLoadingId(suggestion?.SuggestionID);
        try {
            if (action === 'Accepted') {
                setRecommendedTaskPrefill({
                    Title: suggestion?.SuggestionTitle || '',
                    Description: suggestion?.SuggestionDesc || '',
                    FocusAreaID: suggestion?.FocusAreaID ? String(suggestion.FocusAreaID) : '',
                    FocusArea: suggestion?.FocusArea || 'General'
                });
                setPendingAcceptedRecommendation({ kind: 'coach', suggestionId: suggestion?.SuggestionID });
                setShowAddTaskModal(true);
                return;
            }

            await api.put(`/mentor/coach-suggestions/${suggestion?.SuggestionID}/respond`, { action });
            setCoachRecommendations((previous) => previous.map((item) => (
                item?.SuggestionID === suggestion?.SuggestionID
                    ? { ...item, Status: action }
                    : item
            )));
        } catch (err) {
            console.error(`Failed to ${action.toLowerCase()} coach recommendation:`, err);
        } finally {
            setCoachActionLoadingId(null);
        }
    };

    const renderCoachRecommendationCard = (suggestion) => {
        const isPending = isPendingCoachRecommendation(suggestion);
        const statusLabel = getCoachRecommendationStatusLabel(suggestion);
        const normalizedStatus = String(suggestion?.Status || '').trim().toLowerCase();
        const isAccepted = normalizedStatus === 'accepted';
        const isDenied = normalizedStatus === 'denied';
        const isLoading = coachActionLoadingId === suggestion.SuggestionID;

        return (
            <div
                key={suggestion.SuggestionID}
                className={`rounded-lg border px-4 py-4 ${
                    isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-amber-100'
                }`}
            >
                <div className="flex flex-wrap items-start gap-3">
                    <button
                        type="button"
                        title={String(suggestion?.FocusArea || 'General')}
                        aria-label={`Coach focus area initial ${getFocusAreaInitial(suggestion)}`}
                        className={`mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded border border-2 border-amber-500 text-xs font-bold ${
                            isDarkMode ? 'text-slate-300 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                    >
                        {getFocusAreaInitial(suggestion)}
                    </button>
                    <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            {suggestion.SuggestionTitle}
                        </div>
                        {suggestion.SuggestionDesc && (
                            <div className={`mt-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                {suggestion.SuggestionDesc}
                            </div>
                        )}
                        <div className={`mt-2 text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Status: {statusLabel}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end mt-1 sm:mt-0">
                        <button
                            type="button"
                            onClick={() => handleRespondToCoachRecommendation(suggestion, 'Accepted')}
                            disabled={isLoading || !isPending}
                            className={`rounded border border-2 py-1.5 px-3 text-xs font-semibold transition-opacity ${
                                isAccepted
                                    ? isDarkMode
                                        ? 'border-primary-500 opacity-100'
                                        : 'border-primary-500 opacity-100'
                                    : isDenied
                                        ? isDarkMode
                                            ? 'border-primary-500 text-slate-500 opacity-50'
                                            : 'border-primary-500 text-slate-400 opacity-50'
                                        : isDarkMode
                                            ? 'border-primary-500 text-slate-300 hover:bg-primary-500 disabled:bg-transparent disabled:text-slate-500 disabled:opacity-50'
                                            : 'border-primary-500 text-slate-700 hover:bg-primary-300 disabled:bg-transparent disabled:text-slate-400 disabled:opacity-50'
                            }`}
                        >
                            Accept
                        </button>
                        <button
                            type="button"
                            onClick={() => handleRespondToCoachRecommendation(suggestion, 'Denied')}
                            disabled={isLoading || !isPending}
                            className={`rounded border border-2 py-1.5 px-3 text-xs font-semibold transition-opacity ${
                                isDenied
                                    ? isDarkMode
                                        ? 'border-rose-400 opacity-100'
                                        : 'border-rose-400 opacity-100'
                                    : isAccepted
                                        ? isDarkMode
                                            ? 'border-rose-400 text-slate-500 opacity-50'
                                            : 'border-rose-400 text-slate-400 opacity-50'
                                        : isDarkMode
                                            ? 'border-rose-400 text-slate-300 hover:bg-rose-500 disabled:bg-transparent disabled:text-slate-500 disabled:opacity-50'
                                            : 'border-rose-400 text-slate-700 hover:bg-rose-200 disabled:bg-transparent disabled:text-slate-400 disabled:opacity-50'
                            }`}
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const loadDashboardData = async () => {
            setLoading(true);
            try {
                // Don't call /user/me if AuthContext already has user data
                // This prevents duplicate calls and race conditions
                // These four are independent of each other — fire them together
                // instead of awaiting one at a time (each pays its own DB
                // connection cost, so sequential awaits were stacking up).
                const [userRes] = await Promise.all([
                    api.get("/user/me"),
                    fetchTasks(),
                    fetchRecommendedTasks(),
                    fetchDashboardPanels(),
                ]);
                const userData = userRes.data;

                const accountRole = userData.isAdmin ? 'Admin'
                    : userData.isEmployer ? 'Employer'
                    : userData.isCoach ? 'Coach'
                    : 'Client';

                setUserProfile({
                    name: `${userData.first_name} ${userData.last_name}`,
                    profilePicture: userData.profilePicture || null,
                    info: [
                        userData.email,
                        `Account Type: ${accountRole}`,
                        userData.onboarding?.aspiring_role ? `Aspiring Role: ${userData.onboarding.aspiring_role}` : '',
                        `Persona: ${userData.onboarding?.persona || 'Not Set'}`,
                        (userData.onboarding?.industries || []).length > 0 ? `Industry: ${userData.onboarding.industries.join(', ')}` : ''
                    ].filter(Boolean)
                });

                if (lookups && lookups.focusAreas && lookups.focusAreas.length > 0) {
                    const transformedFocusAreas = lookups.focusAreas.map(fa => ({
                        FocusAreaID: fa.id,
                        FocusAreaName: fa.name
                    }));
                    setFocusAreas(transformedFocusAreas);
                } else {
                    try {
                        const faRes = await api.get("/meta/lookups");
                        const transformedFocusAreas = faRes.data.focusAreas.map(fa => ({
                            FocusAreaID: fa.id,
                            FocusAreaName: fa.name
                        }));
                        setFocusAreas(transformedFocusAreas);
                    } catch (err) {
                        console.error("Error fetching focus areas:", err);
                        setFocusAreas([]);
                    }
                }

            } catch (err) {
                console.error("Error loading dashboard:", err);
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData().then(() => {
            // Check for pending surveys once dashboard has loaded
            if (!surveyChecked.current) {
                surveyChecked.current = true;
                const isFirstLogin = searchParams.get('first_login') === 'true';
                const isSurveyLink = searchParams.get('survey') === 'manual';
                const context = isFirstLogin ? 'signup' : isSurveyLink ? 'manual' : 'dashboard';
                checkSurveys(context);
            }
        });
    }, [lookups]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setGoals(buildGoalCounts(tasks));
    }, [tasks]);

    if (loading) {
        return <DashboardSkeleton isDarkMode={isDarkMode} />;
    }

  return (
      <div className={`min-h-screen w-full ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
          <div className="max-w-7xl mx-auto px-4 py-8">
              {/* SECTION 1: Profile + Goals */}
              <section className="mb-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Profile Card */}
                      <div className={`rounded-2xl border shadow-sm p-4 md:p-6 ${
                          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                          <div className="flex items-center gap-4 mb-4">
                              {userProfile.profilePicture && !avatarBroken ? (
                                  <img
                                      src={userProfile.profilePicture}
                                      alt="Profile"
                                      onError={() => setAvatarBroken(true)}
                                      className="w-16 h-16 rounded-full object-cover border-2 shadow-md"
                                      style={{
                                          borderColor: isDarkMode ? '#5A7CDE' : '#4A66B6'
                                      }}
                                  />
                              ) : (
                                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                      isDarkMode ? 'bg-slate-700 border-2 border-primary-500' : 'bg-primary-50 border-2 border-primary-600'
                                  }`}>
                                      <User className={isDarkMode ? 'text-primary-400' : 'text-primary-600'} size={32} />
                                  </div>
                              )}
                              <div>
                                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                      {userProfile.name}
                                  </h3>
                              </div>
                          </div>
                          <div className="space-y-2">
                              {userProfile.info.map((item, idx) => (
                                  <p key={idx} className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                      {item}
                                  </p>
                              ))}
                          </div>
                      </div>

                      {/* Goal Tracker Card */}
                      <div className={`lg:col-span-2 rounded-2xl border shadow-sm p-4 md:p-6 ${
                          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                          <div className="flex items-center gap-2 mb-6">
                              <Target className={isDarkMode ? 'text-primary-400' : 'text-primary-600'} size={20} />
                              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                  Goal Tracker
                              </h3>
                          </div>
                          <div className="space-y-4">
                              {goals.map((goal, index) => (
                                  <div key={index} className="flex items-center gap-4">
                                      <span className={`text-sm font-medium w-24 md:w-32 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                          {goal.name}
                                      </span>
                                      <div className={`flex-1 h-6 rounded-full overflow-hidden ${
                                          isDarkMode ? 'bg-slate-700' : 'bg-slate-100'
                                      }`}>
                                          <div 
                                              className={`h-full transition-all duration-500 ease-out ${
                                                  isDarkMode ? 'bg-primary-600' : 'bg-primary-500'
                                              }`}
                                              style={{ width: `${(goal.count / maxGoalCount) * 100}%` }}
                                          />
                                      </div>
                                      <span className={`text-sm font-semibold w-10 md:w-16 text-right ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                          {goal.count}
                                      </span>
                                  </div>
                              ))}
                          </div>
                      </div>
                  </div>
              </section>

              {/* SECTION 2: ONBOARDING TASKS - Amber tint background (hidden once all onboarding checks are complete) */}
              {!allOnboardingTasksDone && (
              <section className="mb-8">
                  <div className={`rounded-2xl border shadow-sm overflow-hidden ${
                      isDarkMode
                          ? 'bg-slate-800/50 border-slate-700'
                          : 'bg-amber-50/30 border-amber-100'
                  }`}>
                      <div className={`px-4 py-3 md:px-6 md:py-4 border-b ${
                          isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-amber-100 bg-amber-50/50'
                      }`}>
                          <h2 className={`text-lg md:text-2xl font-bold flex items-center gap-2 ${
                              isDarkMode ? 'text-slate-100' : 'text-slate-900'
                          }`}>
                              <Target className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} size={24} />
                              Onboarding Tasks
                          </h2>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-6">
                        <div className="p-4 md:p-6 md:pl-10 md:col-span-2">
                            <div className="text-base md:text-lg font-bold py-2 md:py-4">
                                Welcome to MyBizBrand!
                            </div>
                            <div>
                                Complete the following tasks to set up and launch your website portfolio.
                            </div>
                            <div className="pt-5">
                                <button
                                    type="button"
                                    onClick={handleCompleteOnboardingClick}
                                    className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                                        isDarkMode
                                            ? 'bg-amber-500 text-slate-900 hover:bg-amber-400'
                                            : 'bg-amber-600 text-white hover:bg-amber-700'
                                    }`}
                                >
                                    {`Complete Onboarding (${onboardingDoneCount}/${onboardingTotalCount})`}
                                </button>
                            </div>
                        </div>
                        <div className="md:col-span-4 p-4 md:p-6 pt-0 md:pt-6">
                          {/* <div className={`hidden md:grid grid-cols-12 gap-4 px-4 py-3 text-xs font-semibold uppercase border-b mb-4 ${
                              isDarkMode ? 'text-slate-400 border-slate-700' : 'text-slate-500 border-slate-200'
                          }`}>
                              <span className="col-span-10">Details</span>
                              <span className="col-span-2 text-right">Status</span>
                          </div> */}

                          <div className="space-y-2">
                              {visibleOnboardingTasks.length > 0 ? visibleOnboardingTasks.map((task, idx) => (
                                  <div
                                      key={task.id || idx}
                                      className={`grid grid-cols-1 md:grid-cols-12 gap-4 items-center px-4 py-4 rounded-lg transition-colors ${
                                          idx % 2 === 0
                                              ? isDarkMode
                                                  ? 'bg-slate-800/50 hover:bg-slate-800'
                                                  : 'bg-white hover:bg-amber-50/50'
                                              : isDarkMode
                                                  ? 'bg-slate-800/30 hover:bg-slate-800'
                                                  : 'bg-amber-50/20 hover:bg-amber-50/40'
                                      }`}
                                  >
                                      <div className="md:col-span-10">
                                          <div className={`truncate ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                              {task.note || 'No additional details provided.'}
                                          </div>
                                      </div>
                                      <div className={`md:col-span-2 md:text-right font-semibold ${task.isComplete ? (isDarkMode ? 'text-primary-400' : 'text-primary-600') : task.isAssigned ? (isDarkMode ? 'text-amber-300' : 'text-amber-700') : (isDarkMode ? 'text-slate-300' : 'text-slate-600')}`}>
                                          {task.status || (task.isAssigned ? 'In Progress' : 'Incomplete')}
                                      </div>
                                  </div>
                              )) : (
                                  <div className={`px-4 py-6 rounded-lg text-sm ${isDarkMode ? 'text-slate-400 bg-slate-800/40' : 'text-slate-600 bg-white'}`}>
                                      No onboarding tasks available.
                                  </div>
                              )}
                          </div>
                        </div>
                      </div>
                  </div>
              </section>
              )}

              {/* SECTION 3: TASK LIST - Blue tint background */}
              <section className="mb-8">
                  <div className={`rounded-2xl border shadow-sm overflow-hidden ${
                      isDarkMode
                          ? 'bg-slate-800/50 border-slate-700'
                          : 'bg-blue-50/30 border-blue-100'
                  }`}>
                      <div className={`px-4 py-3 md:px-6 md:py-4 border-b ${
                          isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-blue-100 bg-blue-50/50'
                      }`}>
                          <div className="flex items-center justify-between">
                              <h2 className={`text-lg md:text-2xl font-bold flex items-center gap-2 ${
                                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                              }`}>
                                  <Briefcase className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} size={24} />
                                  My Action Items
                              </h2>
                              <div className="flex items-center gap-3 flex-wrap">
                                  <button type="button" className={btnOutline(isDarkMode)} onClick={handleAddTask}>+ Add Task</button>
                              </div>
                          </div>
                      </div>

                      <div className="p-4 md:p-6">
                          {/* Mobile task cards */}
                          <div className="md:hidden space-y-2">
                              {visibleTasks.length > 0 ? visibleTasks.map((task, idx) => (
                                  <div
                                      key={task.TaskID || idx}
                                      className={`rounded-lg px-4 py-2.5 ${idx % 2 === 0 ? (isDarkMode ? 'bg-slate-800/50' : 'bg-white') : (isDarkMode ? 'bg-slate-800/30' : 'bg-blue-50/20')}`}
                                  >
                                      <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{task.Title}</div>
                                      <div className={`mt-0.5 flex flex-wrap gap-x-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          <span>{task.FocusAreaName}</span>
                                          <span>{normalizeDifficultyLabel(task.DifficultyLevel)}</span>
                                          <span>{task.DueDate ? new Date(task.DueDate).toLocaleDateString() : 'N/A'}</span>
                                      </div>
                                  </div>
                              )) : <Tasks tasks={visibleTasks} isDarkMode={isDarkMode} />}
                          </div>

                          {/* Column Headers */}
                          <div className="hidden md:block overflow-x-auto">
                          <div className={`grid min-w-[760px] grid-cols-5 gap-4 px-4 py-3 text-xs font-semibold uppercase border-b mb-4 ${
                              isDarkMode ? 'text-slate-100 border-slate-700' : 'text-slate-500 border-slate-200'
                          }`}>
                              <span className="col-span-2">Task</span>
                              <span>Focus Area</span>
                              <span>Difficulty</span>
                              <span>Due Date</span>
                          </div>

                          {/* Task Rows */}
                          {visibleTasks.length > 0 ? (
                              <div className="space-y-2 min-w-[760px]">
                                  {visibleTasks.map((task, idx) => (
                                      <div
                                          key={task.TaskID || idx}
                                          className={`grid grid-cols-5 gap-4 items-center px-4 py-4 rounded-lg transition-colors ${
                                              idx % 2 === 0
                                                  ? isDarkMode
                                                      ? 'bg-slate-800/50 hover:bg-slate-800'
                                                      : 'bg-white hover:bg-blue-50/50'
                                                  : isDarkMode
                                                      ? 'bg-slate-800/30 hover:bg-slate-800'
                                                      : 'bg-blue-50/20 hover:bg-blue-50/40'
                                          }`}
                                      >
                                          <div className={`col-span-2 min-w-0 truncate font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                              {task.Title}
                                          </div>
                                          <div className={`whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                              {task.FocusAreaName}
                                          </div>
                                          <div className={`whitespace-nowrap font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                              {normalizeDifficultyLabel(task.DifficultyLevel)}
                                          </div>
                                          <div className={`whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              {task.DueDate ? new Date(task.DueDate).toLocaleDateString() : 'N/A'}
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          ) : (
                              <Tasks tasks={visibleTasks} isDarkMode={isDarkMode} />
                          )}
                          </div>
                      </div>
                  </div>
              </section>

              {/* SECTION 4: RECOMMENDED JOBS - Green tint background */}
              <section className="mb-8">
                  <div className={`rounded-2xl border shadow-sm overflow-hidden ${
                      isDarkMode 
                          ? 'bg-slate-800/50 border-slate-700' 
                          : 'bg-primary-50/30 border-primary-100'
                  }`}>
                      <div className={`px-4 py-3 md:px-6 md:py-4 border-b ${
                          isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-primary-100 bg-primary-50/50'
                      }`}>
                          <h2 className={`text-lg md:text-2xl font-bold flex items-center gap-2 ${
                              isDarkMode ? 'text-slate-100' : 'text-slate-900'
                          }`}>
                              <Briefcase className={isDarkMode ? 'text-primary-400' : 'text-primary-600'} size={24} />
                              Recommended Jobs
                          </h2>
                      </div>

                      <div className="p-4 md:p-6">
                          {/* Column Headers */}
                          <div className={`hidden md:grid grid-cols-5 gap-4 px-4 py-3 text-xs font-semibold uppercase border-b mb-4 ${
                              isDarkMode ? 'text-slate-100 border-slate-700' : 'text-slate-500 border-slate-200'
                          }`}>
                              <span className="col-span-2">Job Title</span>
                              <span>Company</span>
                              <span>Location</span>
                              <span className="text-right">Match</span>
                          </div>

                          {/* Job Rows */}
                          <div className="space-y-2">
                              {recommendedJobs.map((job, idx) => {
                                  const rowBg = idx % 2 === 0
                                      ? isDarkMode ? 'bg-slate-800/50 hover:bg-slate-800' : 'bg-white hover:bg-primary-50/50'
                                      : isDarkMode ? 'bg-slate-800/30 hover:bg-slate-800' : 'bg-primary-50/20 hover:bg-primary-50/40';
                                  return (
                                  <div key={job.id || idx} className={`rounded-lg transition-colors ${rowBg}`}>
                                      {/* Mobile compact card */}
                                      <button
                                          type="button"
                                          onClick={() => handleOpenRecommendedJob(job)}
                                          className="md:hidden w-full text-left px-4 py-3 flex items-center justify-between gap-3"
                                      >
                                          <div className="min-w-0">
                                              <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{job.title}</div>
                                              <div className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{job.company} · {job.location}</div>
                                          </div>
                                          <div className={`shrink-0 text-sm font-semibold ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>{job.match}</div>
                                      </button>

                                      {/* Desktop table row */}
                                      <div className="hidden md:grid grid-cols-5 gap-4 items-center px-4 py-4">
                                          <div className="col-span-2">
                                              <button
                                                  type="button"
                                                  onClick={() => handleOpenRecommendedJob(job)}
                                                  className={`font-medium text-left transition-colors ${isDarkMode ? 'text-slate-300 hover:text-primary-300' : 'text-slate-700 hover:text-primary-700'}`}
                                              >
                                                  {job.title}
                                              </button>
                                          </div>
                                          <div className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                                              {job.company}
                                          </div>
                                          <div className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                                              {job.location}
                                          </div>
                                          <div className={`text-right font-semibold ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                                              {job.match}
                                          </div>
                                      </div>
                                  </div>
                                  );
                              })}
                          </div>

                          <button 
                              className={`mt-4 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                                  isDarkMode
                                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                              }`}
                              onClick={() => navigate('/jobbrowser')}
                          >
                              View More Jobs
                          </button>
                      </div>
                  </div>
              </section>

              {/* SECTION 5: UPCOMING MEETUP EVENTS - Purple tint background */}
              <section className="mb-8">
                  <div className={`rounded-2xl border shadow-sm overflow-hidden ${
                      isDarkMode 
                          ? 'bg-slate-800/50 border-slate-700' 
                          : 'bg-purple-50/30 border-purple-100'
                  }`}>
                      <div className={`px-4 py-3 md:px-6 md:py-4 border-b ${
                          isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-purple-100 bg-purple-50/50'
                      }`}>
                          <h2 className={`text-lg md:text-2xl font-bold flex items-center gap-2 ${
                              isDarkMode ? 'text-slate-100' : 'text-slate-900'
                          }`}>
                              <Calendar className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} size={24} />
                              Upcoming Meetup Events
                          </h2>
                      </div>

                      <div className="p-4 md:p-6">
                          <div className="flex flex-col items-center justify-center text-center py-10">
                              <Calendar className={isDarkMode ? 'text-slate-600' : 'text-slate-300'} size={32} />
                              <p className={`mt-3 font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                  Meetup events are coming soon.
                              </p>
                          </div>
                      </div>
                  </div>
              </section>

              {/* SECTION 5: RECOMMENDED TASKS - Amber tint background */}
              <section id="recommended-tasks" className="mb-8">
                  <div className={`rounded-2xl border shadow-sm overflow-hidden ${
                      isDarkMode 
                          ? 'bg-slate-800/50 border-slate-700' 
                          : 'bg-amber-50/30 border-amber-100'
                  }`}>
                      <div className={`px-4 py-3 md:px-6 md:py-4 border-b ${
                          isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-amber-100 bg-amber-50/50'
                      }`}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <h2 className={`text-lg md:text-2xl font-bold flex items-center gap-2 ${
                                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                              }`}>
                                  <Target className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} size={24} />
                                  Recommended Tasks
                              </h2>
                              <button
                                  type="button"
                                  onClick={handleRegenerateRecommendedTasks}
                                  disabled={isRegeneratingRecommendations}
                                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                                      isDarkMode
                                          ? 'bg-amber-500 text-slate-900 hover:bg-amber-600 disabled:bg-amber-700 disabled:text-slate-300'
                                          : 'bg-amber-500 text-white hover:bg-amber-600 disabled:bg-amber-300 disabled:text-amber-50'
                                  }`}
                              >
                                  {isRegeneratingRecommendations ? 'Regenerating...' : 'Regenerate Tasks'}
                              </button>
                          </div>
                      </div>

                      <div className="p-4 md:p-6">
                          <h3 className={`text-sm font-semibold uppercase mb-3 ${isDarkMode ? 'text-slate-100' : 'text-slate-700'}`}>
                            BB Recommendations
                          </h3>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {recommendedSections.map((section) => (
                                  <div key={section.key}>
                                      <div className="space-y-2">
                                          {recommendedTasks[section.key].length === 0 && (
                                              <div className={`px-4 py-3 rounded-lg border ${
                                                  isDarkMode ? 'bg-slate-800/50 border-slate-700 text-slate-100' : 'bg-white border-amber-100 text-slate-500'
                                              }`}>
                                                  No recommendations available yet.
                                              </div>
                                          )}
                                          {recommendedTasks[section.key].map((task, idx) => (
                                              <div 
                                                  key={`${section.key}-${idx}`} 
                                                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                                                      isDarkMode 
                                                          ? 'bg-slate-800/50 border-slate-700' 
                                                          : 'bg-white border-amber-100'
                                                  }`}
                                              >
                                                  <button
                                                    type="button"
                                                    title={String(task?.FocusAreaDescription || task?.FocusAreaDesciption || task?.focusAreaDescription || task?.area || 'General')}
                                                    aria-label={`Focus area initial ${getFocusAreaInitial(task)}`}
                                                    className={`inline-flex h-7 w-7 items-center justify-center rounded border border-2 border-amber-500 text-xs font-bold ${
                                                            isDarkMode ? 'text-slate-300 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-100'
                                                    }`}
                                                  >
                                                    {getFocusAreaInitial(task)}
                                                  </button>
                                                  <span className={`flex-1 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                      {stripTaskPrefix(task.task)}
                                                  </span>
                                                  <button
                                                      type="button"
                                                      onClick={() => handleAcceptRecommendedTask(section.key, idx, task)}
                                                      className={`rounded border border-2 border-primary-500 py-1.5 px-3 text-xs font-semibold bg-opacity-75 ${
                                                          isDarkMode
                                                              ? 'text-slate-300 hover:bg-primary-500'
                                                              : 'text-slate-700 hover:bg-primary-300'
                                                      }`}
                                                  >
                                                      Accept
                                                      {/* <Check size={16} /> */}
                                                  </button>
                                              </div>
                                          ))}
                                      </div>
                                  </div>
                              ))}
                          </div>

                          <div className="mt-8">
                              <h3 className={`text-sm font-semibold uppercase mb-3 ${isDarkMode ? 'text-slate-100' : 'text-slate-700'}`}>
                                  Coach Recommendations
                              </h3>
                              <div className="space-y-3">
                                  {coachRecommendations.filter(isPendingCoachRecommendation).length === 0 && (
                                      <div className={`px-4 py-3 rounded-lg border ${
                                          isDarkMode ? 'bg-slate-800/50 border-slate-700 text-slate-100' : 'bg-white border-amber-100 text-slate-500'
                                      }`}>
                                          No coach recommendations available right now.
                                      </div>
                                  )}
                                  {coachRecommendations.filter(isPendingCoachRecommendation).map(renderCoachRecommendationCard)}
                              </div>
                          </div>
                      </div>
                  </div>
              </section>

          </div>
      {/* Add Task Modal */}
        <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={handleCloseAddTaskModal}
        onTaskCreated={handleTaskCreated}
        initialTaskData={recommendedTaskPrefill}
        />
        {/* Onboarding Celebration */}
        <OnboardingCelebration
          isOpen={showCelebration}
          onClose={() => setShowCelebration(false)}
        />
        {/* Survey Modal */}
        {pendingSurvey && (
            <SurveyModal
                surveyData={pendingSurvey}
                onComplete={completeSurvey}
                onDismiss={() => dismissSurvey(pendingSurvey.SurveyID)}
                dismissible={true}
            />
        )}
    </div>
  );
};

export default Dashboard;
